import { useEffect, useState, createContext, useContext } from "react";
import { api } from "@/lib/api";
import { Lock, Sparkle, Warning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { openPaywall } from "@/components/PaywallModal";

const TenantCtx = createContext({ tenant: null, reload: () => {} });

// Axios interceptor — intercepts 402 trial_expired responses and opens the paywall
let _interceptorInstalled = false;
function _installBillingInterceptor() {
  if (_interceptorInstalled) return;
  _interceptorInstalled = true;
  api.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : null;
      if (status === 402 && code === "trial_expired") {
        openPaywall("trial_expired");
        // Rewrite the toast-friendly message so it doesn't render as [object]
        if (err.response.data) err.response.data.detail = detail.message || "Trial expired";
      }
      return Promise.reject(err);
    }
  );
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const reload = async () => {
    try { const { data } = await api.get("/tenant"); setTenant(data); return true; }
    catch { setTenant(null); return false; }
  };
  useEffect(() => {
    _installBillingInterceptor();
    // Skip entirely on public magic-link routes — those pages don't have
    // (and shouldn't need) an auth token, and the retry loop was spamming
    // /api/tenant with 401s on the renter portal.
    const path = window.location.pathname || "";
    const publicPrefixes = ["/rent/", "/rent-lease/", "/rent-apply/", "/portal/", "/report/", "/sub/",
                             "/cleaner/", "/tradesman/", "/landlord/", "/apply/",
                             "/pest", "/hvac", "/roofing", "/home-care",
                             "/login", "/signup", "/for-", "/handymen", "/plumbers", "/roofers"];
    const isPublic = publicPrefixes.some(p =>
      path === p || path.startsWith(p + "/") || (p.endsWith("/") ? false : path === p)
    );
    if (isPublic || !localStorage.getItem("access_token")) {
      // Still listen for auth-changed so a login inside the SPA hydrates the tenant.
      const onAuth = () => reload();
      window.addEventListener("auth-changed", onAuth);
      return () => window.removeEventListener("auth-changed", onAuth);
    }
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    // Retry a few times if the token isn't ready yet — but don't loop forever.
    const attempt = async () => {
      if (cancelled) return;
      attempts += 1;
      const ok = await reload();
      if (!ok && !cancelled && attempts < 3) timer = setTimeout(attempt, 1500);
    };
    attempt();
    // Also reload when the auth token changes in another tab / on login/logout.
    const onStorage = (e) => { if (e.key === "token" || e.key === null) reload(); };
    window.addEventListener("storage", onStorage);
    const onAuth = () => reload();
    window.addEventListener("auth-changed", onAuth);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth-changed", onAuth);
    };
  }, []);
  return <TenantCtx.Provider value={{ tenant, reload }}>{children}</TenantCtx.Provider>;
}

export function useTenantCtx() { return useContext(TenantCtx); }

/** Returns { aiEnabled, plan, trialDaysLeft, isTrial, loaded }. Falls back to safe defaults. */
export function useAI() {
  const { tenant } = useTenantCtx();
  if (!tenant) return { aiEnabled: false, plan: "starter", trialDaysLeft: 0, isTrial: false, loaded: false };
  // plan may be missing on legacy tenants — fall back to plan_tier, then treat
  // grandfathered (beta) accounts as pro so we never nag them for an upgrade.
  const raw = tenant.plan || tenant.plan_tier;
  const grandfathered = tenant.subscription_status === "grandfathered";
  const plan = raw || (grandfathered ? "pro" : "starter");
  const aiEnabled = !!tenant.ai_enabled || plan === "pro" || grandfathered;
  const isTrial = plan === "trial";
  let trialDaysLeft = 0;
  if (isTrial && tenant.trial_ends_at) {
    const ms = new Date(tenant.trial_ends_at).getTime() - Date.now();
    trialDaysLeft = Math.max(0, Math.ceil(ms / 86400000));
  }
  return { aiEnabled, plan, trialDaysLeft, isTrial, loaded: true };
}

/** Subscription/billing state derived from the tenant record. */
export function useSubscription() {
  const { tenant } = useTenantCtx();
  if (!tenant) return { status: "grandfathered", daysLeft: null, expired: false, inTrial: false };
  const status = tenant.subscription_status || "grandfathered";
  return {
    status,
    daysLeft: tenant.subscription_days_left,
    trialEndsAt: tenant.subscription_trial_ends_at,
    expired: status === "expired",
    inTrial: status === "trial",
    active: status === "active",
    grandfathered: status === "grandfathered",
  };
}

/** Gate wrapper — click either fires onGo (if AI ok) or shows the upgrade toast. */
export function AIGatedButton({ onGo, children, className = "", "data-testid": testid, ...rest }) {
  const { aiEnabled } = useAI();
  const cls = aiEnabled ? className : `${className} opacity-70 relative`;
  return (
    <button
      data-testid={testid}
      onClick={(e) => {
        if (!aiEnabled) {
          e.preventDefault();
          toast.error("Pro feature — upgrade to unlock AI", {
            action: { label: "Upgrade", onClick: () => window.location.href = "/admin/branding" }
          });
          return;
        }
        onGo && onGo(e);
      }}
      className={cls}
      {...rest}
    >
      {!aiEnabled && <Lock weight="fill" size={12} className="inline mr-1"/>}
      {children}
    </button>
  );
}

export function TrialBanner() {
  const { tenant } = useTenantCtx();
  const { isTrial, trialDaysLeft, aiEnabled, plan, loaded } = useAI();
  const sub = useSubscription();

  // Never flash while tenant is still loading — avoids the "AI-only" strip
  // showing on grandfathered/Pro accounts during the initial API round-trip.
  if (!loaded || !tenant) return null;
  // Grandfathered (beta / owner-tier) accounts never see upgrade nags.
  if (sub.grandfathered) return null;

  // Subscription banner takes precedence over AI trial banner
  if (sub.inTrial && sub.daysLeft != null) {
    const urgent = sub.daysLeft <= 2;
    return (
      <div
        className={`${urgent ? "bg-gradient-to-r from-red-600 to-amber-500" : "bg-gradient-to-r from-violet-600 to-[#00C4CC]"} text-white px-4 py-2 flex items-center justify-between text-xs sm:text-sm font-bold`}
        data-testid="subscription-trial-banner">
        <span className="flex items-center gap-2">
          <Sparkle weight="fill" size={16}/>
          {sub.daysLeft} day{sub.daysLeft === 1 ? "" : "s"} left in your free trial · Add payment to continue
        </span>
        <Link to="/admin/branding" data-testid="subscription-upgrade-link" className="underline uppercase tracking-wider hover:opacity-80">Add Payment →</Link>
      </div>
    );
  }
  if (sub.expired) {
    return (
      <div className="bg-red-700 text-white px-4 py-2 flex items-center justify-between text-xs sm:text-sm font-bold" data-testid="subscription-expired-banner">
        <span className="flex items-center gap-2">
          <Warning weight="fill" size={16}/>
          Trial ended — read-only mode. Add a payment method to unlock edits.
        </span>
        <Link to="/admin/branding" data-testid="subscription-expired-link" className="underline uppercase tracking-wider hover:opacity-80">Subscribe →</Link>
      </div>
    );
  }

  if (plan === "pro") return null;
  if (isTrial && aiEnabled && trialDaysLeft > 0) {
    return (
      <div className="bg-gradient-to-r from-violet-600 to-[#00C4CC] text-white px-4 py-2 flex items-center justify-between text-xs sm:text-sm font-bold" data-testid="trial-banner">
        <span className="flex items-center gap-2">
          <Sparkle weight="fill" size={16}/>
          {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left in your Pro trial
        </span>
        <Link to="/admin/branding" className="underline uppercase tracking-wider hover:opacity-80">Upgrade Now →</Link>
      </div>
    );
  }
  if (!aiEnabled) {
    return (
      <div className="bg-slate-900 text-white px-4 py-2 flex items-center justify-between text-xs sm:text-sm font-bold" data-testid="ai-locked-banner">
        <span className="flex items-center gap-2">
          <Lock weight="fill" size={14}/>
          {plan === "trial" ? "Trial expired — AI features locked" : "AI features are Pro-only"}
        </span>
        <Link to="/admin/branding" className="underline uppercase tracking-wider text-[#00C4CC] hover:opacity-80">Upgrade →</Link>
      </div>
    );
  }
  return null;
}
